import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  FileVideo,
  KeyRound,
  Loader2,
  Trash2,
  TriangleAlert,
  UploadCloud,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Layout } from '@/components/Layout';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { useVideoUpload, type ServerProgress } from '@/hooks/useVideoUpload';
import { formatBytes } from '@/lib/blossomUpload';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import { getVideoNip19, parseVideoEvent } from '@/lib/videoTypes';

type UploadStage = 'form' | 'uploading' | 'done';

export default function UploadPage() {
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const navigate = useNavigate();
  const upload = useVideoUpload();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [cwEnabled, setCwEnabled] = useState(false);
  const [cwReason, setCwReason] = useState('');
  const [useAddressable, setUseAddressable] = useState(true);
  const [anonymous, setAnonymous] = useState(false);

  const [stage, setStage] = useState<UploadStage>('form');
  const [serverProgress, setServerProgress] = useState<ServerProgress[]>([]);
  const currentServerRef = useRef<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof upload.mutateAsync>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyKept, setKeyKept] = useState(false);

  const servers = getEffectiveBlossomServers(config.blossomServerMetadata, config.useAppBlossomServers);

  // Overall progress = average across servers
  const overall = useMemo(() => {
    if (serverProgress.length === 0) return 0;
    const sum = serverProgress.reduce((s, p) => s + p.progress.percent, 0);
    return Math.round(sum / serverProgress.length);
  }, [serverProgress]);

  // The "current" server = the one that most recently reported progress
  const currentServer = useMemo(() => {
    if (currentServerRef.current) {
      const match = serverProgress.find(p => p.serverUrl === currentServerRef.current);
      if (match && (match.status === 'uploading' || match.status === 'pending')) return match;
    }
    const uploading = serverProgress.find(p => p.status === 'uploading');
    return uploading ?? serverProgress.find(p => p.status === 'done') ?? serverProgress[0] ?? null;
  }, [serverProgress]);

  const onFile = (selected: File | null) => {
    if (!selected) return;
    if (!selected.type.startsWith('video/') && !selected.name.match(/\.(mp4|webm|mov|mkv|avi|m4v|m3u8)$/i)) {
      setError('Please select a video file.');
      return;
    }
    setError(null);
    setFile(selected);
    if (!title) {
      setTitle(selected.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
    }
  };

  const submit = async () => {
    if (!file || !title.trim()) return;
    setError(null);
    setStage('uploading');
    setServerProgress([]);

    try {
      const tags = hashtags
        .split(/[,\s]+/)
        .map(t => t.replace(/^#/, '').trim().toLowerCase())
        .filter(Boolean);

      const res = await upload.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        file,
        thumbnailFile: thumbnailFile ?? undefined,
        contentWarning: cwEnabled && cwReason.trim() ? cwReason.trim() : undefined,
        hashtags: tags,
        useAddressable,
        anonymous: anonymous || !user,
        onServerProgress: (updated, all) => {
          currentServerRef.current = updated.serverUrl;
          setServerProgress(all.map(s => ({ ...s, progress: { ...s.progress } })));
        },
      });
      setResult(res);
      setStage('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setStage('form');
    }
  };

  const resultVideo = result ? parseVideoEvent(result.event) : null;
  const resultLink = resultVideo ? `/${getVideoNip19(resultVideo)}` : null;

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-3 sm:px-6 py-8">
        <h1 className="mb-6 text-2xl font-bold">Upload video</h1>

        {stage === 'done' && result ? (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Check className="h-5 w-5 text-green-500" /> Video published
                </CardTitle>
                <CardDescription>
                  Uploaded to {result.videoServerResults.filter(r => r.success).length} of{' '}
                  {result.videoServerResults.length} Blossom servers and published to your relays.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  {result.videoServerResults.map((r) => (
                    <div key={r.serverUrl} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-muted-foreground">{new URL(r.serverUrl).hostname}</span>
                      {r.success ? (
                        <Badge variant="secondary" className="text-green-600 dark:text-green-400">OK</Badge>
                      ) : (
                        <Badge variant="destructive">Failed</Badge>
                      )}
                    </div>
                  ))}
                </div>

                {resultLink && (
                  <Button asChild className="w-full">
                    <Link to={resultLink}>Watch your video</Link>
                  </Button>
                )}
              </CardContent>
            </Card>

            {result.anonymousKey && !keyKept && (
              <Card className="border-yellow-600/40">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <KeyRound className="h-5 w-5 text-yellow-500" /> One-time key
                  </CardTitle>
                  <CardDescription>
                    This video was published with a fresh, one-time Nostr key.
                    Save it if you want to edit or delete this video later —
                    it cannot be recovered. Or toss it and stay anonymous.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      type={keyRevealed ? 'text' : 'password'}
                      value={result.anonymousKey.nsec}
                      className="font-mono text-xs"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setKeyRevealed(v => !v)}
                      aria-label={keyRevealed ? 'Hide key' : 'Reveal key'}
                    >
                      {keyRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={async () => {
                        await navigator.clipboard.writeText(result.anonymousKey!.nsec);
                        setKeyCopied(true);
                        setTimeout(() => setKeyCopied(false), 1500);
                      }}
                      aria-label="Copy key"
                    >
                      {keyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" className="flex-1" onClick={() => setKeyKept(true)}>
                      I've saved this key — keep it
                    </Button>
                    <Button
                      variant="destructive"
                      className="gap-1.5"
                      onClick={() => {
                        setKeyKept(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" /> Toss it
                    </Button>
                  </div>
                  {resultLink && (
                    <p className="text-xs text-muted-foreground">
                      Video address:{' '}
                      <button
                        type="button"
                        className="font-mono underline"
                        onClick={async () => {
                          await navigator.clipboard.writeText(nip19.npubEncode(result.anonymousKey!.pubkey));
                        }}
                      >
                        copy npub
                      </button>
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Button variant="outline" onClick={() => navigate('/')} className="w-full">
              Back to feed
            </Button>
          </div>
        ) : stage === 'uploading' ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Uploading…</CardTitle>
              <CardDescription>
                Your video is being uploaded to {servers.length} Blossom server{servers.length === 1 ? '' : 's'} simultaneously.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Overall progress */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Overall progress</span>
                  <span className="tabular-nums text-muted-foreground">{overall}%</span>
                </div>
                <Progress value={overall} className="h-3" />
              </div>

              {/* Current server progress */}
              {currentServer && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">
                      Current server:{' '}
                      <span className="text-muted-foreground">{new URL(currentServer.serverUrl).hostname}</span>
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {currentServer.progress.percent}%
                    </span>
                  </div>
                  <Progress value={currentServer.progress.percent} className="h-2" />
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {formatBytes(currentServer.progress.loaded)} / {formatBytes(currentServer.progress.total)}
                  </p>
                </div>
              )}

              {/* All servers */}
              <div className="space-y-2 border-t border-border pt-4">
                {serverProgress.map((s) => (
                  <div key={s.serverUrl} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate text-muted-foreground">{new URL(s.serverUrl).hostname}</span>
                      <span className="tabular-nums">
                        {s.status === 'done' ? (
                          <span className="text-green-600 dark:text-green-400 font-medium">Done</span>
                        ) : s.status === 'failed' ? (
                          <span className="text-destructive font-medium">Failed</span>
                        ) : (
                          `${s.progress.percent}%`
                        )}
                      </span>
                    </div>
                    <Progress
                      value={s.status === 'done' ? 100 : s.progress.percent}
                      className="h-1.5"
                    />
                    {s.error && <p className="text-xs text-destructive">{s.error}</p>}
                  </div>
                ))}
              </div>

              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Don't close this tab until the upload completes.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {/* File picker */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onFile(e.dataTransfer.files?.[0] ?? null);
              }}
              className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-card p-10 text-center transition-colors hover:border-primary/50 hover:bg-accent/50"
            >
              {file ? (
                <>
                  <FileVideo className="h-10 w-10 text-primary" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-sm text-muted-foreground">{formatBytes(file.size)}</span>
                  <span className="text-xs text-muted-foreground">Click or drop to choose a different file</span>
                </>
              ) : (
                <>
                  <UploadCloud className="h-10 w-10 text-muted-foreground" />
                  <span className="font-medium">Drop a video file here, or click to browse</span>
                  <span className="text-xs text-muted-foreground">mp4, webm, mov, mkv, m4v…</span>
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,.mkv,.m3u8"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />

            <div className="space-y-1.5">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My awesome video"
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this video about?"
                rows={4}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="hashtags">Hashtags</Label>
              <Input
                id="hashtags"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="nostr, bitcoin, art (comma or space separated)"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="thumbnail">Custom thumbnail (optional)</Label>
              <Input
                id="thumbnail"
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnailFile(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">
                If omitted, a frame is extracted from the video automatically.
              </p>
            </div>

            {/* Content warning (NIP-36) */}
            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="cw" className="flex items-center gap-2 cursor-pointer">
                  <TriangleAlert className="h-4 w-4 text-yellow-500" />
                  Content warning (NIP-36)
                </Label>
                <Switch id="cw" checked={cwEnabled} onCheckedChange={setCwEnabled} />
              </div>
              {cwEnabled && (
                <Input
                  value={cwReason}
                  onChange={(e) => setCwReason(e.target.value)}
                  placeholder="Reason, e.g. flashing lights, sensitive topic…"
                />
              )}
              <p className="text-xs text-muted-foreground">
                The thumbnail will be hidden behind a warning until viewers choose to reveal it.
              </p>
            </div>

            {/* Event type */}
            <div className="flex items-start gap-2.5 rounded-lg border border-border p-4">
              <Checkbox
                id="addressable"
                checked={useAddressable}
                onCheckedChange={(c) => setUseAddressable(c === true)}
                className="mt-0.5"
              />
              <Label htmlFor="addressable" className="cursor-pointer font-normal">
                <span className="font-medium">Addressable event (kind 34235/34236)</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Recommended: lets you edit the video metadata later. Uncheck for a
                  regular event (kind 21/22) which can't be edited.
                </span>
              </Label>
            </div>

            {/* Key choice */}
            {user ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-border p-4">
                <Checkbox
                  id="anonymous"
                  checked={anonymous}
                  onCheckedChange={(c) => setAnonymous(c === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="anonymous" className="cursor-pointer font-normal">
                  <span className="font-medium flex items-center gap-1.5">
                    <KeyRound className="h-3.5 w-3.5" /> Publish with a one-time key
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    A fresh key is generated for this video only. You can keep it
                    (to edit/delete later) or toss it after publishing.
                  </span>
                </Label>
              </div>
            ) : (
              <Alert>
                <KeyRound className="h-4 w-4" />
                <AlertTitle>Anonymous upload</AlertTitle>
                <AlertDescription>
                  You're not logged in, so a fresh one-time key will be generated for
                  this video. After publishing you can keep the key (to edit or delete
                  the video later) or toss it.
                </AlertDescription>
              </Alert>
            )}

            {servers.length === 0 && (
              <Alert variant="destructive">
                <TriangleAlert className="h-4 w-4" />
                <AlertDescription>
                  No Blossom servers configured.{' '}
                  <Link to="/settings" className="underline">Add one in Settings</Link> first.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <TriangleAlert className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={submit}
              disabled={!file || !title.trim() || servers.length === 0 || upload.isPending}
              className="w-full"
              size="lg"
            >
              Publish video
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
